import React from 'react';
import { useChildren } from '../context/ChildProvider';
import { Button } from '../components/UI';
import SkeletonLoader from '../components/SkeletonLoader';
import { calculateAge } from '../helpers/utils';
import { motion } from 'framer-motion';

// --- Futuristic UI Components ---

const FuturisticQuickAction = ({ label, icon, onClick, glowColor, description }) => (
    <motion.button 
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.95 }}
        onClick={onClick} 
        className={`
            group relative overflow-hidden rounded-[1.5rem] p-5 text-left border border-white/40
            bg-white/40 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.04)]
            hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:border-white/60 hover:bg-white/60
            transition-all duration-500
        `}
    >
        <div className={`absolute -right-8 -top-8 w-28 h-28 bg-${glowColor}-300 rounded-full mix-blend-multiply filter blur-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500`}></div>
        <div className={`absolute -left-8 -bottom-8 w-28 h-28 bg-${glowColor}-200 rounded-full mix-blend-multiply filter blur-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500`}></div>
        
        <div className="relative z-10">
            <div className={`w-12 h-12 rounded-2xl bg-white/70 flex items-center justify-center text-2xl mb-4 shadow-sm border border-white group-hover:scale-110 transition-transform duration-500 group-hover:rotate-3`}>
                {icon}
            </div>
            <h3 className="font-extrabold text-gray-800 text-[15px] mb-1 tracking-tight">{label}</h3>
            <p className="text-[11px] text-gray-500 font-medium leading-tight">{description}</p>
        </div>
    </motion.button>
);

const GlassStatsCard = ({ icon, title, value, subtitle, glowColor }) => (
    <div className={`
        relative overflow-hidden rounded-[1.5rem] p-5 border border-white/50
        bg-white/40 backdrop-blur-xl
        shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-lg transition-all duration-300
    `}>
        <div className={`absolute -right-10 -top-10 w-32 h-32 bg-${glowColor}-300 rounded-full mix-blend-multiply filter blur-2xl opacity-30`}></div>
        <div className={`absolute -left-10 -bottom-10 w-32 h-32 bg-${glowColor}-100 rounded-full mix-blend-multiply filter blur-2xl opacity-40`}></div>
        
        <div className="relative z-10 flex flex-col h-full justify-between">
            <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-[1rem] bg-white/80 flex items-center justify-center text-lg shadow-sm border border-white`}>
                    {icon}
                </div>
                <span className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-br from-gray-800 to-gray-500 drop-shadow-sm">{value}</span>
            </div>
            <div>
                <h4 className="font-bold text-gray-800 text-[13px] tracking-tight">{title}</h4>
                <p className="text-[10px] text-gray-500 font-medium mt-0.5">{subtitle}</p>
            </div>
        </div>
    </div>
);

const FuturisticChildCard = ({ child, navigate }) => {
    const { lastAssessment } = child;
    const age = calculateAge(child.dob.toDate());
    
    const getStatusInfo = () => {
        if (!lastAssessment || !lastAssessment.overallStatus) {
            return { text: 'Belum Diperiksa', icon: '❓', color: 'gray', ring: 'border-gray-200', glow: 'shadow-gray-200/50' };
        }
        switch (lastAssessment.overallStatus) {
            case 'Sesuai': 
            case 'Normal':
                return { text: 'Normal', icon: '✅', color: 'emerald', ring: 'border-emerald-300', glow: 'shadow-emerald-200/50' };
            case 'Meragukan': 
                return { text: 'Meragukan', icon: '⚠️', color: 'amber', ring: 'border-amber-300', glow: 'shadow-amber-200/50' };
            case 'Ada Penyimpangan': 
            case 'Penyimpangan':
                return { text: 'Penyimpangan', icon: '🚨', color: 'rose', ring: 'border-rose-300', glow: 'shadow-rose-200/50' };
            case 'Perlu Rujukan Segera':
                return { text: 'Rujukan Segera', icon: '🆘', color: 'red', ring: 'border-red-400', glow: 'shadow-red-300/50' };
            default: 
                return { text: 'Selesai', icon: '✅', color: 'cyan', ring: 'border-cyan-300', glow: 'shadow-cyan-200/50' };
        }
    };
    
    const status = getStatusInfo();
    const childEmoji = child.gender === 'Laki-laki' ? '👦' : child.gender === 'Perempuan' ? '👧' : '👶';
    
    return (
        <div 
            onClick={() => navigate('viewChild', child)}
            className={`
                min-w-[280px] w-[85%] snap-center group relative overflow-hidden rounded-[2rem] p-5 cursor-pointer
                bg-white/60 backdrop-blur-2xl border border-white/80
                shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:shadow-xl hover:${status.glow} transition-all duration-500
                transform hover:-translate-y-1 active:scale-95 flex-shrink-0
            `}
        >
            <div className={`absolute -right-10 top-0 w-40 h-40 bg-${status.color}-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 group-hover:opacity-60 transition-opacity duration-500 pointer-events-none`}></div>
            
            <div className="flex items-center space-x-4 relative z-10">
                <div className="relative">
                    <div className={`w-16 h-16 rounded-[1.25rem] bg-white flex items-center justify-center text-3xl shadow-sm border-2 ${status.ring}`}>
                        {childEmoji}
                    </div>
                    {(status.color === 'rose' || status.color === 'amber' || status.color === 'red') && (
                        <div className={`absolute inset-0 rounded-[1.25rem] border-2 ${status.ring} animate-ping opacity-60`}></div>
                    )}
                </div>
                
                <div className="flex-grow">
                    <div className="flex justify-between items-start">
                        <h3 className="font-extrabold text-lg text-gray-800 tracking-tight leading-tight line-clamp-1">{child.name}</h3>
                        {child.isShared && (
                            <span className="px-2 py-0.5 bg-indigo-100/80 text-indigo-700 text-[9px] rounded-lg font-bold uppercase tracking-wider border border-indigo-200 ml-2">
                                Shared
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 font-medium mt-0.5">{age}</p>
                    {(child.schoolName || child.className) && (
                        <p className="text-[10px] text-gray-400 font-medium mt-0.5 truncate">
                            {child.schoolName || 'PAUD/Sekolah tidak diketahui'} {child.className ? `• Kelas ${child.className}` : ''}
                        </p>
                    )}
                    
                    <div className="mt-3 flex flex-col space-y-1">
                        <div className={`self-start px-2.5 py-1 rounded-lg bg-${status.color}-100/80 backdrop-blur-sm text-${status.color}-700 text-[10px] font-bold border border-${status.color}-200 flex items-center space-x-1`}>
                            <span>{status.icon}</span>
                            <span>{status.text}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SectionHeader = ({ icon, title }) => (
    <div className="flex items-center space-x-2 mb-4 px-1">
        <div className="w-8 h-8 rounded-lg bg-white/50 backdrop-blur-sm flex items-center justify-center text-lg shadow-sm border border-white/50">
            {icon}
        </div>
        <h3 className="text-lg font-bold text-gray-800 tracking-tight">{title}</h3>
    </div>
);

// --- Dashboards ---

const ParentDashboard = ({ myChildren, navigate, showToast }) => {
    const totalChildren = myChildren.length;
    const assessedChildren = myChildren.filter(child => child.lastAssessment?.overallStatus).length;
    const needsAttention = myChildren.filter(child => 
        ['Ada Penyimpangan', 'Meragukan', 'Penyimpangan', 'Perlu Rujukan Segera'].includes(child.lastAssessment?.overallStatus)
    ).length;
    const upToDate = myChildren.filter(child => 
        ['Sesuai', 'Normal'].includes(child.lastAssessment?.overallStatus)
    ).length;

    return (
        <div className="space-y-10">
            {/* 1. Children List (Carousel) First */}
            <div>
                <SectionHeader icon="👨‍👩‍👧‍👦" title={`Anak Anda (${totalChildren})`} />
                {myChildren.length > 0 ? (
                    <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-6 pt-2 px-1 -mx-5 px-5 scrollbar-hide">
                        {myChildren.map(child => (
                            <FuturisticChildCard key={child.id} child={child} navigate={navigate} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-10 px-6 bg-white/40 backdrop-blur-xl rounded-[2.5rem] border border-white/60 shadow-sm">
                        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl mx-auto mb-4 shadow-sm">👶</div>
                        <h3 className="text-lg font-bold text-gray-800 mb-2">Belum Ada Data Anak</h3>
                        <p className="text-xs text-gray-500 mb-6 leading-relaxed">Mulai pantau tumbuh kembang anak sekarang.</p>
                        <Button onClick={() => navigate('addChild')} className="bg-gray-900 text-white w-full py-4 rounded-xl shadow-xl">
                            ➕ Tambah Anak
                        </Button>
                    </div>
                )}
            </div>

            {/* 2. Quick Actions */}
            <div>
                <SectionHeader icon="🚀" title="Aksi Cepat" />
                <div className="grid grid-cols-2 gap-4">
                    <FuturisticQuickAction label="Tambah Anak" icon="👶" description="Daftarkan profil baru" glowColor="purple" onClick={() => navigate('addChild')} />
                    <FuturisticQuickAction label="Pemeriksaan" icon="🔍" description="Mulai skrining" glowColor="cyan" onClick={() => navigate('assessmentHub')} />
                    <FuturisticQuickAction label="Stimulasi" icon="🎯" description="Kegiatan seru" glowColor="emerald" onClick={() => navigate('stimulation')} />
                    <FuturisticQuickAction label="Edukasi" icon="📚" description="Artikel kesehatan" glowColor="orange" onClick={() => navigate('education')} />
                </div>
            </div>

            {/* 3. Stats at the bottom */}
            {totalChildren > 0 && (
                <div>
                    <SectionHeader icon="📊" title="Ringkasan Statistik" />
                    <div className="grid grid-cols-2 gap-4">
                        <GlassStatsCard icon="👶" title="Total Anak" value={totalChildren} subtitle="Terdaftar" glowColor="blue" />
                        <GlassStatsCard icon="🔍" title="Diperiksa" value={assessedChildren} subtitle="Sudah skrining" glowColor="emerald" />
                        {needsAttention > 0 && <GlassStatsCard icon="⚠️" title="Perhatian" value={needsAttention} subtitle="Tindak lanjut" glowColor="rose" />}
                        {upToDate > 0 && <GlassStatsCard icon="✅" title="Sesuai" value={upToDate} subtitle="Perkembangan normal" glowColor="cyan" />}
                    </div>
                </div>
            )}
        </div>
    );
};

const NakesDashboard = ({ allPatients, navigate }) => {
    const childrenNeedingAttention = allPatients.filter(child => 
        ['Meragukan', 'Ada Penyimpangan', 'Penyimpangan', 'Perlu Rujukan Segera'].includes(child.lastAssessment?.overallStatus)
    ).sort((a, b) => {
        const urgent = ['Perlu Rujukan Segera', 'Ada Penyimpangan'];
        const aUrgent = urgent.includes(a.lastAssessment?.overallStatus);
        const bUrgent = urgent.includes(b.lastAssessment?.overallStatus);
        if (aUrgent && !bUrgent) return -1;
        if (!aUrgent && bUrgent) return 1;
        return 0;
    });

    const stuntingRisk = allPatients.filter(child => 
        child.lastAssessment?.pertumbuhan?.status?.includes('Kurang') || 
        child.lastAssessment?.pertumbuhan?.status?.includes('Buruk') ||
        child.lastAssessment?.pertumbuhan?.status === 'Perlu Rujukan Segera'
    );

    const normalChildren = allPatients.filter(child => ['Sesuai', 'Normal'].includes(child.lastAssessment?.overallStatus));

    return (
        <div className="space-y-10">
            {/* Status Kritis / Stunting */}
            {stuntingRisk.length > 0 && (
                <div className="bg-rose-50/80 border border-rose-200 rounded-[2rem] p-5 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <div className="w-10 h-10 rounded-full bg-rose-200 flex items-center justify-center text-xl shadow-sm border border-rose-300">🚨</div>
                        <h3 className="text-lg font-bold text-rose-800 tracking-tight">Risiko Gizi / Stunting ({stuntingRisk.length})</h3>
                    </div>
                    <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-2 scrollbar-hide">
                        {stuntingRisk.map(child => (
                            <FuturisticChildCard key={child.id} child={child} navigate={navigate} />
                        ))}
                    </div>
                </div>
            )}

            {childrenNeedingAttention.length > 0 && (
                <div>
                    <SectionHeader icon="⚠️" title={`Perlu Pantauan Lanjutan (${childrenNeedingAttention.length})`} />
                    <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-6 pt-2 px-1 -mx-5 px-5 scrollbar-hide">
                        {childrenNeedingAttention.map(child => (
                            <FuturisticChildCard key={child.id} child={child} navigate={navigate} />
                        ))}
                    </div>
                </div>
            )}

            <div>
                <SectionHeader icon="👩‍⚕️" title="Aksi Cepat Klinis" />
                <div className="grid grid-cols-2 gap-4">
                    <FuturisticQuickAction label="Rekapan Klinis" icon="📋" description="Lihat seluruh pasien" glowColor="rose" onClick={() => navigate('rekapan')} />
                    <FuturisticQuickAction label="Akses Kode" icon="📤" description="Data dari ortu" glowColor="indigo" onClick={() => navigate('accessSharedData')} />
                    <FuturisticQuickAction label="Skrining" icon="🔍" description="Lakukan tes" glowColor="cyan" onClick={() => navigate('assessmentHub')} />
                    <FuturisticQuickAction label="Tambah Pasien" icon="👶" description="Profil baru" glowColor="purple" onClick={() => navigate('addChild')} />
                </div>
            </div>
            
            {normalChildren.length > 0 && (
                <div>
                    <SectionHeader icon="✅" title={`Perkembangan Normal (${normalChildren.length})`} />
                    <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-6 pt-2 px-1 -mx-5 px-5 scrollbar-hide">
                        {normalChildren.map(child => (
                            <FuturisticChildCard key={child.id} child={child} navigate={navigate} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const TeacherDashboard = ({ allPatients, navigate }) => {
    // Group children by class
    const classGroups = allPatients.reduce((acc: any, child: any) => {
        const className = child.className || 'Tanpa Kelas';
        if (!acc[className]) acc[className] = [];
        acc[className].push(child);
        return acc;
    }, {} as Record<string, any[]>);

    return (
        <div className="space-y-10">
            <div>
                <SectionHeader icon="👩‍🏫" title="Aksi Cepat Guru" />
                <div className="grid grid-cols-2 gap-4">
                    <FuturisticQuickAction label="Tambah Murid" icon="👶" description="Daftarkan murid baru" glowColor="purple" onClick={() => navigate('addChild')} />
                    <FuturisticQuickAction label="Input Massal" icon="⚖️" description="Input BB/TB Sekaligus" glowColor="emerald" onClick={() => navigate('batchMeasurement')} />
                    <FuturisticQuickAction label="Skrining" icon="🔍" description="Kuesioner per anak" glowColor="cyan" onClick={() => navigate('assessmentHub')} />
                    <FuturisticQuickAction label="Akses Data" icon="📤" description="Minta kode berbagi" glowColor="indigo" onClick={() => navigate('accessSharedData')} />
                </div>
            </div>

            {Object.keys(classGroups).length > 0 ? (
                Object.entries(classGroups).map(([className, children]: [string, any[]]) => (
                    <div key={className} className="bg-white/40 backdrop-blur-xl border border-white/50 rounded-[2rem] p-5 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-lg shadow-sm border border-blue-200">🏫</div>
                                <h3 className="text-lg font-bold text-gray-800 tracking-tight">{className} ({children.length} Anak)</h3>
                            </div>
                            <button 
                                onClick={() => import('../helpers/pdfGenerator').then(m => m.generateBatchReportCardsPDF(className, children))}
                                className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg border border-indigo-200 transition-colors flex items-center space-x-1"
                            >
                                <span>🖨️</span>
                                <span>Cetak Laporan</span>
                            </button>
                        </div>
                        <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-2 scrollbar-hide">
                            {children.map(child => (
                                <FuturisticChildCard key={child.id} child={child} navigate={navigate} />
                            ))}
                        </div>
                    </div>
                ))
            ) : (
                <div className="text-center py-12 px-6 bg-white/40 backdrop-blur-xl rounded-[2.5rem] border border-white/60">
                    <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl mx-auto mb-6 shadow-sm">🏫</div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Data Murid Kosong</h3>
                    <p className="text-sm text-gray-500 mb-8">Minta kode berbagi dari orang tua murid untuk mulai memantau.</p>
                    <Button onClick={() => navigate('accessSharedData')} className="bg-gray-900 text-white w-full py-4 rounded-xl shadow-xl">Akses Kode Berbagi</Button>
                </div>
            )}
        </div>
    );
};

const GuestDashboard = ({ navigate, showToast }) => {
    return (
        <div className="space-y-10">
            <div className="bg-white/60 backdrop-blur-2xl rounded-[2rem] p-6 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.05)] text-center relative overflow-hidden">
                <div className="absolute -right-10 -top-10 w-40 h-40 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
                <div className="relative z-10">
                    <div className="w-20 h-20 bg-white rounded-[1.5rem] flex items-center justify-center text-4xl mx-auto mb-4 shadow-sm border border-gray-100">👤</div>
                    <h3 className="font-extrabold text-xl text-gray-800 mb-2">Mode Penjelajah</h3>
                    <p className="text-sm text-gray-500 mb-6 leading-relaxed">Ini adalah Mode Simulasi. Anda bebas melihat antarmuka dan mencoba fitur, namun data Anda tidak akan disimpan.</p>
                    <Button onClick={() => window.location.reload()} className="bg-gray-900 text-white hover:bg-gray-800 w-full rounded-2xl py-3 shadow-xl">
                        Daftar / Keluar Mode Simulasi
                    </Button>
                </div>
            </div>

            <div>
                <SectionHeader icon="🚀" title="Coba Fitur Kami" />
                <div className="grid grid-cols-2 gap-4">
                    <FuturisticQuickAction label="Pemeriksaan" icon="🔍" description="Simulasi skrining" glowColor="cyan" onClick={() => navigate('assessmentHub')} />
                    <FuturisticQuickAction label="Edukasi" icon="📚" description="Artikel kesehatan" glowColor="emerald" onClick={() => navigate('education')} />
                </div>
            </div>
        </div>
    );
};

export default function HomeScreen({ user, navigate, showToast }) {
    const { myChildren, sharedChildren, loading } = useChildren();

    const getUserDisplayName = () => {
        if (user.isAnonymous) return 'Pengguna Tamu';
        const name = user.username || user.email || 'Pengguna';
        return name.split(' ').slice(0, 2).join(' '); // Return up to 2 words for cleaner UI
    };

    const getGreetingDetails = () => {
        const hour = new Date().getHours();
        if (hour < 11) return { text: 'Selamat Pagi,', icon: '🌅', gradient: 'from-orange-500 to-yellow-500' };
        if (hour < 15) return { text: 'Selamat Siang,', icon: '☀️', gradient: 'from-sky-500 to-blue-500' };
        if (hour < 19) return { text: 'Selamat Sore,', icon: '🌇', gradient: 'from-indigo-500 to-purple-500' };
        return { text: 'Selamat Malam,', icon: '🌙', gradient: 'from-blue-700 to-indigo-900' };
    };
    
    const greeting = getGreetingDetails();

    const renderDashboard = () => {
        if (user.isAnonymous) return <GuestDashboard navigate={navigate} showToast={showToast} />;
        if (user.role === 'Tenaga Kesehatan') {
            return <NakesDashboard allPatients={[...sharedChildren, ...myChildren]} navigate={navigate} />;
        }
        if (user.role === 'Guru PAUD') {
            return <TeacherDashboard allPatients={[...sharedChildren, ...myChildren]} navigate={navigate} />;
        }
        return <ParentDashboard myChildren={myChildren} navigate={navigate} showToast={showToast} />;
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-purple-200">
            {/* --- Futuristic Animated Background --- */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-purple-300 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob"></div>
                <div className="absolute top-[20%] right-[-10%] w-[40vw] h-[40vw] bg-cyan-300 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-[-10%] left-[20%] w-[60vw] h-[60vw] bg-pink-300 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-4000"></div>
                <div className="absolute inset-0 bg-white/30 backdrop-blur-[50px]"></div>
            </div>

            <div className="relative z-10 pb-28 px-5 pt-10 max-w-md mx-auto min-h-screen flex flex-col">
                {/* --- Sophisticated Glass Header --- */}
                <div className="flex items-center justify-between mb-8 bg-white/40 backdrop-blur-2xl rounded-[2rem] p-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-white/60">
                    <div className="flex items-center space-x-4">
                        <div className={`w-14 h-14 rounded-[1.25rem] bg-gradient-to-br ${greeting.gradient} text-white flex items-center justify-center text-2xl shadow-lg transform hover:rotate-6 transition-transform duration-300`}>
                            {greeting.icon}
                        </div>
                        <div>
                            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">{greeting.text}</p>
                            <h1 className="text-2xl font-extrabold text-gray-800 tracking-tight leading-none">
                                {getUserDisplayName()}
                            </h1>
                            {user.isAnonymous && (
                                <span className="inline-block mt-1.5 px-2 py-0.5 bg-gray-900 text-white text-[9px] rounded-md font-bold uppercase tracking-wider">Mode Tamu</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* --- Main Content Area --- */}
                <div className="flex-grow">
                    {loading && !user.isAnonymous ? (
                        <div className="space-y-6 bg-white/50 backdrop-blur-xl rounded-[2rem] p-6 border border-white">
                            <SkeletonLoader count={3} />
                        </div>
                    ) : (
                        <div className="animate-fade-in-up">
                            {renderDashboard()}
                        </div>
                    )}
                </div>
            </div>
            
            <style>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
                .animation-delay-2000 { animation-delay: 2s; }
                .animation-delay-4000 { animation-delay: 4s; }
                
                @keyframes fade-in-up {
                    0% { opacity: 0; transform: translateY(20px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                
                /* Hide scrollbar for Chrome, Safari and Opera */
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                /* Hide scrollbar for IE, Edge and Firefox */
                .scrollbar-hide {
                    -ms-overflow-style: none;  /* IE and Edge */
                    scrollbar-width: none;  /* Firefox */
                }
            `}</style>
        </div>
    );
}
